<template>
    <div class="white-box white-box-no-tab pt-2.5 md:py-5 md:px-8">
        <div class="text-lg xs:text-xl md:text-[1.3125rem] text-gray-900 font-bold mb-3.5 md:mb-7">
            保險單借款
        </div>
        <div class="text-base xs:text-lg text-green-600 font-bold mb-2.5">
            請選擇保單幣別
        </div>
        <div class="pb-3.5 border-b bnorder-gray-200">
            <Multiselect
                v-model="currentCurrency"
                :options="currencyList"
                class="select-base max-w-[23.375rem] ml-0"
                placeholder="請選擇"
                :can-clear="false"
                :can-deselect="false"
                :object="true"
                no-options-text="無匯幣別資料"
                @select="selectCurrency()"
            />
        </div>
        <div
            v-if="currentPolicyList.length !== 0"
            class="mt-4 md:mt-2.5 mb-2.5 border-b border-gray-200 pb-3.5"
        >
            <div class="text-base xs:text-lg text-green-600 font-bold">
                可借款明細
            </div>
            <ul class="text-tip mb-2.5">
                <li>提醒您，為維護資料正確完整，每次申請的保單數上限為5張，且每張單次保險單借款限額為約當新台幣100萬(含)。</li>
            </ul>
            <div
                v-if="loanedMsgText.length !== 0"
                class="bg-gray-50 px-2.5 py-1.5 md:py-2.5 text-warning text-sm xs:text-base mb-2.5"
            >
                <p
                    v-for="(text,i) in loanedMsgText"
                    :key="i"
                >
                    {{ text }}
                </p>
            </div>

            <template v-if="currentPolicyList.length === 0">
                <Nodata>無保單資料</Nodata>
            </template>
            <template v-else>
                <div
                    v-if="name"
                    class="text-gray-900 text-base mb-2.5"
                >
                    被保險人：{{ name }}
                </div>
                <div class="lg:hidden pad:grid pad:grid-cols-2 pad:gap-4">
                    <Card
                        v-for="item in currentPolicyList"
                        :key="item.fullPolicyNo"
                        :disabled="!item.loaned"
                        :title="item.fullPolicyNo"
                        :cursor-pointer="false"
                        class="sm:max-w-[30rem] mb-4 last:mb-0 md:mb-0"
                    >
                        <template #topContent>
                            <div class="py-1.5 px-2.5 flex items-center justify-between">
                                <div class="text-sm flex-shrink-0 text-gray-400">
                                    本月利率(年利率)
                                </div>
                                <div class="text-base xs:text-lg text-right text-gray-700">
                                    {{ item.loanRate }}%
                                </div>
                            </div>
                            <div class="py-1.5 px-2.5 flex items-center justify-between">
                                <div class="text-sm flex-shrink-0 text-gray-400">
                                    可借款金額
                                </div>
                                <div class="text-base xs:text-lg text-right text-gray-700">
                                    {{ addComma(item.lastLoanLimit) }}
                                </div>
                            </div>
                        </template>
                        <template #foldContent>
                            <div class="py-1.5 px-2.5 flex items-center justify-between">
                                <div class="text-sm flex-shrink-0 text-gray-400">
                                    契約狀態
                                </div>
                                <div class="text-base xs:text-lg text-right text-gray-700">
                                    {{ item.cnctStat }}
                                </div>
                            </div>
                            <div class="py-1.5 px-2.5 flex items-center justify-between">
                                <div class="text-sm flex-shrink-0 text-gray-400">
                                    已借款本金
                                </div>
                                <div class="text-base xs:text-lg text-right text-gray-700">
                                    {{ addComma(item.lastLoanPrin) }}
                                </div>
                            </div>
                            <div class="py-1.5 px-2.5 flex items-center justify-between">
                                <div class="text-sm flex-shrink-0 text-gray-400">
                                    借款利息
                                </div>
                                <div class="text-base xs:text-lg text-right text-gray-700">
                                    {{ addComma(item.lastLoanInter) }}
                                </div>
                            </div>
                        </template>
                        <template #bottomContent>
                            <div class="pt-3 px-2.5">
                                <div class="flex items-center justify-between">
                                    <div class="text-sm flex-shrink-0 text-gray-700">
                                        欲借款金額
                                    </div>
                                    <div class="text-base xs:text-lg text-right text-gray-700">
                                        <input
                                            v-model.trim="item.amount"
                                            type="text"
                                            placeholder="請輸入欲借款金額"
                                            inputmode="numeric"
                                            autocomplete="off"
                                            maxlength="7"
                                            class="input-base text-right w-[10.625rem] xs:w-[12.5rem]"
                                            @input="validate(item)"
                                        >
                                    </div>
                                </div>
                                <p class="error-msg text-right h-5">
                                    {{ item.errorMsg }}
                                </p>
                            </div>
                        </template>
                    </Card>
                </div>
                <!-- for computer -->
                <Table
                    class="hidden lg:block"
                    :cursor-pointer="false"
                >
                    <template #thead>
                        <tr class="hidden pad:table-row">
                            <td class="">
                                保單號碼
                            </td>
                            <td class="text-center">
                                契約狀態
                            </td>
                            <td class="text-right">
                                已借款本金
                            </td>
                            <td class="text-right">
                                借款利息
                            </td>
                            <td class="text-right">
                                本月利率
                            </td>
                            <td class="text-right">
                                可借款金額
                            </td>
                            <td class="text-right">
                                欲借款金額
                            </td>
                        </tr>
                    </template>
                    <template #tbody>
                        <template
                            v-for="item in currentPolicyList"
                            :key="item.fullPolicyNo"
                        >
                            <tr :class="{'disabled':!item.loaned}">
                                <td class="break-all">
                                    {{ item.fullPolicyNo }}
                                </td>
                                <td class="text-center">
                                    {{ item.cnctStat }}
                                </td>
                                <td class="text-right">
                                    {{ addComma(item.lastLoanPrin) }}
                                </td>
                                <td class="text-right">
                                    {{ addComma(item.lastLoanInter) }}
                                </td>
                                <td class="text-right">
                                    {{ item.loanRate }}%
                                </td>
                                <td class="text-right">
                                    {{ addComma(item.lastLoanLimit) }}
                                </td>
                                <td class="text-right">
                                    <input
                                        v-model.trim="item.amount"
                                        type="text"
                                        placeholder="請輸入欲借款金額"
                                        inputmode="numeric"
                                        autocomplete="off"
                                        maxlength="7"
                                        class="input-base text-right w-[9.625rem] xs:w-[10.625rem]"
                                        :disabled="!item.loaned"
                                        @input="validate(item)"
                                    >
                                    <p class="error-msg text-right">
                                        {{ item.errorMsg }}
                                    </p>
                                </td>
                            </tr>
                        </template>
                    </template>
                </Table>
            </template>
        </div>
        <div class="text-base xs:text-lg text-green-600 font-bold mt-2.5 mb-2.5">
            請選擇指定匯款帳號
        </div>
        <div>
            <Multiselect
                v-model="currentBankInfo"
                :options="currentBankList"
                class="select-base max-w-[23.375rem] pad:max-w-[38rem] ml-0 mb-1 h-[4rem] pad:h-[3rem]"
                placeholder="請選擇指定匯款帳號 "
                :can-clear="false"
                :can-deselect="false"
                :disabled="!currentCurrency.value"
                :object="true"
                no-options-text="無匯款帳號資料"
            >
                <template #singlelabel="{ value }">
                    <div class="w-10/12 md:w-11/12 absolute left-3 items-center flex flex-wrap md:flex-nowrap">
                        <p class="w-full md:w-2/5 truncate md:inline ">
                            {{ value.bankNo }} {{ value.bankName }}
                        </p>
                        <p class="w-full md:w-3/5 truncate md:inline md:overflow-clip">
                            {{ `${value.currencyText}帳戶:${value.bankAccount}` }}
                        </p>
                    </div>
                </template>
                <template #option="{ option }">
                    <div>
                        <p>{{ `銀行代號: ${option.bankNo}` }} <span class="block sm:inline-block">{{ option.bankName }}</span></p>
                        <p>{{ `${option.currencyText}帳戶: ${option.bankAccount}` }}</p>
                    </div>
                </template>
            </Multiselect>
            <ul class="text-tip">
                <li>如欲申請外幣保單借款，但尚未設定外幣匯款帳號，請回到首頁點選「保單變更」進行申辦。</li>
            </ul>
            <ul class="text-tip">
                <li>提醒您勿聽信高報酬、穩賺不賠等「假投資」之詐騙話術，接獲來路不明投資管道、網路連結或加Line投資時，請至警政署165全民防詐網(https://165.npa.gov.tw/)進一步查證真實性，遠離詐騙陷阱，守護您與家人的財物安全。</li>
            </ul>
        </div>
        <div class="mt-7 md:mt-4">
            <TermsSelectAccount />
        </div>

        <Loading :is-loading="isLoading" />
    </div>
    <Bottombar :control-visible="isOk">
        <template #right>
            <button
                class="btn btn-accent rounded-full"
                :disabled="!isOk"
                @click="goNext()"
            >
                下一步
            </button>
        </template>
    </Bottombar>
</template>

<script>
import { computed, ref, inject } from 'vue';
import { addComma } from '@/utils/number/addComma';
import { integer, minMax } from '@/utils/rules/customRules';
import { getInfoPon4300, submitPon4300 } from '@/apis/Pon4300.js';
import { useRouter, useRoute } from 'vue-router';
import { useStore } from 'vuex';
import routeName from '@/router/constant.js';
import Multiselect from '@vueform/multiselect';
import Table from '@/components/table/Table.vue';
import Card from '@/components/card/CardPolicyBase.vue';
import Bottombar from '@/components/layout/Bottombar.vue';
import TermsSelectAccount from '@/components/termContent/TermsSelectAccount.vue';
import Loading from '@/components/Loading.vue';
import Nodata from '@/components/text/EmptyData.vue';

export default {
    components: {
        Multiselect,
        Table,
        Card,
        Bottombar,
        TermsSelectAccount,
        Loading,
        Nodata
    },
    setup() {
        const $vfm = inject('$vfm');
        const router = useRouter();
        const route = useRoute();
        const { code } = route.meta;
        const store = useStore();
        const currentCurrency = ref({});
        const currentBankInfo = ref({});
        const currentPolicyList = ref([]);
        const currentBankList = ref([]);
        const currencyList = ref([]);
        const bankList = ref([]);
        const policyList = ref([]);
        const msgList = ref([]);
        const name = ref('');
        const isLoading = ref(false);
        const result = computed(() => store.state.services.changeItemsResult[code]);
        const getData = () => {
            isLoading.value = true;
            getInfoPon4300().then(res => {
                if (res.data) {
                    bankList.value = res.data.bankList.map(i => {
                        i.value = i.bankAccount;
                        i.label = `銀行代號: ${i.bankNo} ${i.bankName} ${i.currencyText}帳戶: ${i.bankAccount}`;
                        return i;
                    });
                    currencyList.value = res.data.currencyList.map(i => {
                        i.value = i.currency;
                        i.label = `${i.currencyText}(${i.currency})`;
                        return i;
                    });
                    policyList.value = res.data.policyList;
                    msgList.value = res.data.msgList;
                    name.value = res.data.name;

                    let setting = null;
                    /* msgList有值就跳 */
                    if (msgList.value.length !== 0) {
                        setting = {
                            params: {
                                title: res.data.customTitle,
                                content: msgList.value.join('、'),
                                cancelButton: null,
                                confirmButton: res.data.confirmButton,
                                closeicon: false,
                            },
                            on: {
                                confirm() {
                                    router.push({ name: routeName.HOME });
                                    $vfm.hide('DialogBase');
                                }
                            }
                        };
                    } else {
                        setting = {
                            params: {
                                title: '告知事項',
                                content: '依行政院金融監督管理委員會104年6月24日「保險業辦理電子商務應注意事項」，透過網路申請保險單借款僅限要/被保險人同一人，若要/被保險人非同一人，申請保單借款請洽您的保單服務人員或親臨本公司服務櫃台辦理，造成您的不便，敬請見諒！',
                                cancelButton: null,
                                confirmButton: '確認',
                                closeicon: false,
                            },
                            on: {
                                confirm() {
                                    $vfm.hide('DialogBase');
                                }
                            }
                        };
                    }
                    /** 打開告知事項燈箱 */
                    if (setting) {
                        isLoading.value = false;
                        openModal(setting);
                    }
                }
            }).finally(() => {
                isLoading.value = false;
            });
        };

        // store有資料就不打API，直接塞資料
        if (result.value) {
            const {
                storeCurrencyList,
                storeBankList,
                storePolicyList,
                storeMsgList,
                storeName,
                storeCurrentCurrency,
                storeCurrentBankInfo,
                storeCurrentBankList,
                storeCurrentPolicyList
            } = result.value;
            currencyList.value = storeCurrencyList;
            bankList.value = storeBankList;
            policyList.value = storePolicyList;
            msgList.value = storeMsgList;
            name.value = storeName;
            currentCurrency.value = storeCurrentCurrency;
            currentBankInfo.value = storeCurrentBankInfo;
            currentBankList.value = storeCurrentBankList;
            currentPolicyList.value = storeCurrentPolicyList;
        } else {
            getData();
        }

        // 不可借款訊息
        const loanedMsgText = computed(() => {
            const arr = [];
            currentPolicyList.value.forEach(i => {
                if (i.loanedMsg) arr.push(`保單${i.fullPolicyNo}：${i.loanedMsg}`);
            });
            return arr;
        });

        // 選擇保單幣別後篩選保單
        const selectCurrency = () => {
            currentPolicyList.value = [];
            currentBankList.value = [];
            currentBankInfo.value = {};
            const { value } = currentCurrency.value;
            policyList.value.forEach(i => {
                /* 如果幣別為台幣，只給臺幣保單，其他外幣要加入ALL& OBU */
                if (value !== 'NTD') {
                    if (i.currency === value || i.currency === 'ALL' || i.currency === 'OBU') {
                        i.amount = '';
                        currentPolicyList.value.push(i);
                    }
                } else {
                    if (i.currency === value) {
                        i.amount = '';
                        currentPolicyList.value.push(i);
                    }
                }
            });
            bankList.value.forEach(i => {
                if (value !== 'NTD') {
                    if (i.currency === value || i.currency === 'ALL' || i.currency === 'OBU') {
                        currentBankList.value.push(i);
                    }
                } else {
                    if (i.currency === value) {
                        currentBankList.value.push(i);
                    }
                }
            });
        };

        // 借款金額驗證
        const validate = (item) => {
            const errorMsg = integer(item.amount) || minMax(item.amount, 0, item.lastLoanLimit, '', '需低於可借款金額');
            item.errorMsg = errorMsg || '';
        };

        // 是否可以下一步
        const isOk = computed(() => {
            let error = 0;
            let sum = 0;
            currentPolicyList.value.forEach(i => {
                if (i.amount) sum += Number(i.amount);
                if (i.errorMsg) error++;
            });
            if (sum === 0 || isNaN(Number(sum))) error++;
            if (!Object.keys(currentBankInfo.value).length) error++;
            if (!Object.keys(currentCurrency.value).length) error++;
            return error === 0;
        });

        // 下一步
        const goNext = () => {
            isLoading.value = true;
            const returnData = {};
            returnData.bankNo = currentBankInfo.value.bankNo;
            returnData.bankAccount = currentBankInfo.value.bankAccount;
            returnData.pon4300List = [];
            currentPolicyList.value.forEach(i => {
                if (i.amount && i.amount !== 0) {
                    const obj = {};
                    obj.policyNo = i.policyNo;
                    obj.policySeq = i.policySeq;
                    obj.idDup = i.idDup;
                    obj.amount = i.amount;
                    returnData.pon4300List.push(obj);
                }
            });
            submitPon4300(returnData).then(res => {
                const list = {
                    storeCurrencyList: currencyList.value,
                    storeBankList: bankList.value,
                    storePolicyList: policyList.value,
                    storeMsgList: msgList.value,
                    storeName: name.value,
                    storeCurrentCurrency: currentCurrency.value,
                    storeCurrentBankInfo: currentBankInfo.value,
                    storeCurrentBankList: currentBankList.value,
                    storeCurrentPolicyList: currentPolicyList.value,
                };
                store.dispatch('services/setChangeItemsResult', { code, value: list });
                router.push({ name: routeName.CONFIRM_DATA });
            }).finally(() => {
                isLoading.value = false;
            });
        };

        const openModal = (setting) => {
            $vfm.show({
                component: 'DialogBase',
                params: setting.params,
                on: setting.on
            });
        };

        return {
            name,
            currentCurrency,
            currentBankInfo,
            currentPolicyList,
            currentBankList,
            currencyList,
            bankList,
            addComma,
            selectCurrency,
            validate,
            goNext,
            loanedMsgText,
            isOk,
            isLoading
        };
    }
};
</script>

<style lang="scss" scoped>
</style>
